import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Empty, Spin } from '../Components/ui';
import axios from 'axios';
import { useAuthHeader, useAuthUser } from 'react-auth-kit';
import env from 'react-dotenv';
import { Crypter } from '../Functions/Security/Crypter';
import User from '../Models/User';
import datas from "../Assets/data.json";
import { RefreshCw, Monitor, BarChart3, Gauge } from 'lucide-react';
import AppLayout from '../Layout/AppLayout';
import { StatCard } from '../Components/ui';

/** Salutation selon l'heure de la journée. */
const greeting = () => new Date().getHours() < 19 && new Date().getHours() >= 5 ? 'Bonjour' : 'Bonsoir';

/**
 * Tableau de bord — KPI BI résolus.
 * Consomme GET /api/BI/Resolution (KPI effectifs via cascade
 * ABONNE > FILIALE > SERVICE > ROLE > UTILISATEUR) puis
 * GET /api/BI/KpiValeur pour la valeur calculée de chaque KPI.
 */
const Dashboard = () => {

    // USER SESSION
    const auth = useAuthUser()
    const user: User = JSON.parse(JSON.stringify(auth() || {}))
    const navigate = useNavigate()
    const authHeader = useAuthHeader()

    const [resolvedKpis, setResolvedKpis] = useState<any[]>([])
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        LoadKpis()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    /** Résolution des KPI effectifs de l'utilisateur courant. */
    function LoadKpis() {
        setLoading(true)
        axios.request({
            method: 'get',
            maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.BI.Resolution + Crypter.encrypt(user.CodeU),
            headers: { 'Accept': 'text/plain', 'Authorization': authHeader() },
        })
            .then((response: any) => {
                setLoading(false)
                const res = JSON.parse(Crypter.decrypt(response.data)).Value
                if (res?.StatutCode == 200 || res?.StatutCode === undefined) {
                    const list = Array.isArray(res?.Result) ? res.Result
                        : Array.isArray(res?.Result?.Kpis) ? res.Result.Kpis
                        : Array.isArray(res?.Result?.[0]) ? res.Result[0] : []
                    const kpis = list.map((k: any) => ({ ...k, Valeur: undefined, LoadingValeur: true }))
                    setResolvedKpis(kpis)
                    kpis.forEach((k: any) => loadKpiValeur(k.CodeKpi))
                } else {
                    setResolvedKpis([])
                }
            })
            .catch(() => {
                setLoading(false)
                setResolvedKpis([])
            });
    }

    /** Valeur calculée d'un KPI pour le contexte utilisateur. */
    function loadKpiValeur(codeKpi: string) {
        axios.request({
            method: 'get',
            maxBodyLength: Infinity,
            url: env.API_BASE_URL + datas.API_LINKs.BI.KpiValeur + Crypter.encrypt(JSON.stringify({ CodeKpi: codeKpi })),
            headers: { 'Accept': 'text/plain', 'Authorization': authHeader() },
        })
            .then((response: any) => {
                const res = JSON.parse(Crypter.decrypt(response.data)).Value
                const valeur = res?.Result && typeof res.Result === 'object'
                    ? (res.Result.Valeur ?? res.Result.Value ?? res.Result.Montant)
                    : res?.Result
                setResolvedKpis((prev) => prev.map((k: any) =>
                    k.CodeKpi === codeKpi ? { ...k, Valeur: valeur, LoadingValeur: false } : k))
            })
            .catch(() => {
                setResolvedKpis((prev) => prev.map((k: any) =>
                    k.CodeKpi === codeKpi ? { ...k, Valeur: null, LoadingValeur: false } : k))
            })
    }

    const dateStr = new Date().toLocaleDateString('fr-FR', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    })

    return (
        <AppLayout>
            <Spin spinning={loading}>
                <div className="eb-dash">

                    {/* Hero — salutation + actions principales */}
                    <section className="eb-dash-hero">
                        <span className="eb-dash-orb eb-dash-orb-1" />
                        <span className="eb-dash-orb eb-dash-orb-2" />
                        <span className="eb-dash-orb eb-dash-orb-3" />

                        <div className="eb-dash-hero-content">
                            <span className="eb-dash-date">{dateStr}</span>
                            <h1 className="eb-dash-title">
                                {greeting()}, <em>{user?.Name || user?.CodeU}</em>
                            </h1>
                            <p className="eb-dash-sub">
                                Vos indicateurs de pilotage — résolus selon votre contexte
                                (abonné, filiale, service, rôle).
                            </p>
                            <div className="eb-dash-hero-actions">
                                <button className="eb-dash-cta" onClick={() => navigate('/liveScreem')}>
                                    <Monitor size={17} />
                                    Live Screen
                                </button>
                                <button className="eb-dash-cta ghost" onClick={() => navigate('/Stat')}>
                                    <BarChart3 size={16} />
                                    Explorer les statistiques
                                </button>
                                <button className="eb-dash-cta ghost" onClick={LoadKpis}>
                                    <RefreshCw size={15} />
                                </button>
                            </div>
                        </div>

                        {/* Visuel décoratif — écran avec ondes */}
                        <div className="eb-dash-hero-visual" aria-hidden>
                            <span className="eb-dash-ring r1" />
                            <span className="eb-dash-ring r2" />
                            <span className="eb-dash-ring r3" />
                            <Monitor size={84} strokeWidth={1.1} />
                        </div>
                    </section>

                    {/* KPI résolus pour le contexte utilisateur */}
                    <div className="eb-dash-section-head">
                        <h2>Vos indicateurs</h2>
                        <span className="eb-dash-section-note">Cascade ABONNE › FILIALE › SERVICE › ROLE › UTILISATEUR</span>
                    </div>

                    {resolvedKpis.length > 0 ? (
                        <div className="eb-stat-grid">
                            {resolvedKpis.map((k: any) => (
                                <StatCard
                                    key={k.CodeKpi}
                                    label={k.Libelle || k.CodeKpi}
                                    value={k.LoadingValeur ? '…' : (k.Valeur ?? '—')}
                                    sub={k.Unite ? `unité : ${k.Unite}` : k.CodeKpi}
                                    icon={<Gauge />}
                                    tone="primary"
                                />
                            ))}
                        </div>
                    ) : (
                        !loading && (
                            <div className="eb-card">
                                <Empty description="Aucun indicateur n'est affecté à votre profil — contactez un administrateur BI" />
                            </div>
                        )
                    )}

                    <p className="eb-dash-foot">eBCall · Gestion intelligente des files d'attente</p>
                </div>
            </Spin>
        </AppLayout>
    );
};

export default Dashboard;
